/**
 * Branding Context - Gestion dynamique du branding du site
 * Logo, favicon, titre et couleurs du thème depuis le CMS
 * Intégration Uploadcare CDN pour les images
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import config from 'config';

const API_URL = config.API_URL;

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

/**
 * Resolve a stored image path/URL to a displayable URL.
 * Post-Uploadcare migration: only GCS or local paths are valid.
 * Returns null for legacy Uploadcare UUIDs so the caller can use its fallback.
 */
const resolveImageUrl = (value) => {
  if (!value) return null;

  // Full HTTP URL (GCS / external / Uploadcare)
  if (value.startsWith('http')) {
    // Uploadcare is deprecated — discard these so the caller falls back to its default
    if (value.includes('ucarecdn.com') || value.includes('ucarecd.net')) {
      return null;
    }
    return value;
  }

  // Legacy local path via API proxy
  if (value.startsWith('/uploads/')) {
    const filename = value.replace('/uploads/', '');
    return `${API_URL}/api/cms/uploads/${filename}`;
  }

  // Other absolute local path
  if (value.startsWith('/')) {
    return `${API_URL}${value}`;
  }

  // Bare UUIDs (legacy Uploadcare) → ignore, caller fallback will kick in
  return null;
};

/**
 * Apply theme colors to CSS custom properties
 */
const applyThemeColors = (colors) => {
  const root = document.documentElement;
  
  if (colors.primary_color) {
    root.style.setProperty('--color-primary', colors.primary_color);
    root.style.setProperty('--color-gold', colors.primary_color);
  }
  if (colors.secondary_color) {
    root.style.setProperty('--color-secondary', colors.secondary_color);
  }
  if (colors.accent_color) {
    root.style.setProperty('--color-accent', colors.accent_color);
  }
  if (colors.text_color) {
    root.style.setProperty('--color-text', colors.text_color);
  }
  if (colors.background_color) {
    root.style.setProperty('--color-background', colors.background_color);
  }
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({
    logo_url: null,
    favicon_url: null,
    site_title: 'Y GO Shuttle Services France',
    site_tagline: 'Chauffeur privé VTC au Havre',
    primary_color: '#C9A24D',
    secondary_color: '#0B0B0B',
    accent_color: '#E6C77A',
    text_color: '#E5E5E5',
    background_color: '#0B0B0B'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/cms/branding/public`);
        const data = response.data;
        
        setBranding(prev => ({
          ...prev,
          ...data
        }));
        
        // NOTE: document.title is intentionally NOT set here.
        // React Helmet (via <SEO />) owns the per-page <title> to keep unique titles per route.
        
        // Update favicon dynamically (fallback to static icon if value invalid)
        const faviconCandidate = resolveImageUrl(data.favicon_url) || '/icon-192.png';
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconCandidate;
        
        // Apply theme colors to CSS variables
        applyThemeColors(data);
        
      } catch (err) {
        console.error('Failed to fetch branding:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, []);

  // Resolve logo/favicon — fall back to bundled static assets when CMS value is invalid
  const logoUrl = resolveImageUrl(branding.logo_url) || '/ygo-logo.png';
  const faviconUrl = resolveImageUrl(branding.favicon_url) || '/icon-192.png';

  const value = {
    branding,
    loading,
    logoUrl,
    faviconUrl,
    siteTitle: branding.site_title,
    siteTagline: branding.site_tagline,
    primaryColor: branding.primary_color,
    secondaryColor: branding.secondary_color,
    accentColor: branding.accent_color,
    textColor: branding.text_color,
    backgroundColor: branding.background_color,
    getImageUrl: resolveImageUrl
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
